/**
 * Locked Focus Mode Context
 * 
 * When One Thing exists, the system enters LOCKED FOCUS MODE:
 * - Dashboard, Pipeline, Insights become read-only
 * - Only Call/WhatsApp/Mark Done actions are available
 * - Message: "Finché questa azione non è fatta, tutto il resto è rumore."
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { buildEliteTodayPlan } from '@/core/decision-engine/eliteEngine';

interface LockedFocusContextType {
  isLocked: boolean;
  oneThing: {
    id: string;
    leadName: string;
    dealValue: number;
    reasons: string[];
    contact?: {
      telLink?: string;
      whatsappLink?: string;
    };
  } | null;
  unlock: () => void;
}

const LockedFocusContext = createContext<LockedFocusContextType | undefined>(undefined);

export function LockedFocusProvider({ children }: { children: ReactNode }) {
  const [isLocked, setIsLocked] = useState(false);
  const [oneThing, setOneThing] = useState<LockedFocusContextType['oneThing']>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Only fetch leads/deals if we're in an app route (not public routes)
  const isAppRoute = location.pathname.startsWith('/app');
  
  // Always call hooks (React rules), but disable queries for public routes
  // useLeads and useDeals will handle the enabled flag internally via workspaceId
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();

  // Check if One Thing exists
  useEffect(() => {
    if (!leads || !deals || leads.length === 0) {
      setIsLocked(false);
      setOneThing(null);
      return;
    }

    const plan = buildEliteTodayPlan({
      leads,
      deals,
      config: {
        maxColdPerDay: 3,
        staleDealDays: 5,
        mandatoryLimit: 12,
        optionalLimit: 18,
        enableTemperatureDrift: false,
      },
    });

    if (plan.oneThing) {
      setIsLocked(true);
      setOneThing({
        id: plan.oneThing.id,
        leadName: plan.oneThing.lead.name,
        dealValue: plan.oneThing.deal?.value || 0,
        reasons: plan.oneThing.reasons,
        contact: plan.oneThing.contact,
      });
    } else {
      setIsLocked(false);
      setOneThing(null);
    }
  }, [leads, deals]);

  // DISABLED: No longer redirect - let users access Dashboard/Insights
  // The LOCKED FOCUS MODE is now just a visual indicator, not a blocker
  // useEffect(() => {
  //   if (isLocked && oneThing) {
  //     const lockedPaths = ['/app/dashboard', '/app/pipeline', '/app/insights'];
  //     if (lockedPaths.includes(location.pathname)) {
  //       navigate('/app/today', { replace: true });
  //     }
  //   }
  // }, [isLocked, oneThing, location.pathname, navigate]);

  const unlock = () => {
    setIsLocked(false);
    setOneThing(null);
  };

  return (
    <LockedFocusContext.Provider value={{ isLocked, oneThing, unlock }}>
      {children}
    </LockedFocusContext.Provider>
  );
}

export function useLockedFocus() {
  const context = useContext(LockedFocusContext);
  if (!context) {
    throw new Error('useLockedFocus must be used within LockedFocusProvider');
  }
  return context;
}

